import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useRef } from "react";
import api from "../../../../../api/axiosInstance";
import { getResumeFile, onResumeFileChange } from "../../../../../resumeFileStore";
// ─── Colors ──────────────────────────────────────────────────────────────────
const C = {
  ink: "#0d0d0f", paper: "#f9f8f5", cream: "#f2f1ec", warmGray: "#e5e3dc",
  mid: "#8c8680", muted: "#c2bdb4",
  accent: "#c84b31", accentLight: "#faeee9",
  green: "#2a6049", greenLight: "#e6f3ed",
  blue: "#1a4a87", blueLight: "#e5edf8",
  gold: "#a87228", goldLight: "#fdf4e0",
};

// ─── PDF Artifact Cleaner ─────────────────────────────────────────────────────
function cleanPdfText(str) {
  if (!str || typeof str !== "string") return str;
  return str
    .replace(/\bCHANGE\s*PHOTO\b/gi, "")
    .replace(/\bChange\s*Photo\b/g, "")
    .replace(/\bPHOTO\b/g, "")
    .replace(/\bCONTACT\b/gi, "")
    .replace(/^[■✉●•◆▪]+\s*/gm, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

// ─── Skill Validator ──────────────────────────────────────────────────────────
function looksLikeSkill(s) {
  if (!s || typeof s !== "string") return false;
  const trimmed = s.trim();
  if (trimmed.length === 0 || trimmed.length > 60) return false;
  const wordCount = trimmed.split(/\s+/).length;
  if (wordCount > 5) return false;
  const rejectPatterns = [
    /^(good|excellent|strong|basic|advanced|working)\s/i,
    /^(i am|i have|i worked|i did|did a|built a|made a|used |learned|completed|created)/i,
    /communication\s*skills/i,
    /team\s*player/i,
    /quick\s*learner/i,
    /hardworking/i,
    /willing\s*to/i,
    /looking\s*for/i,
    /^(ravi|kumar|photo|change|contact)\b/i,
    /@/,
    /^\d+$/,
    /^[^a-zA-Z]/,
    /fetches\s*weather/i,
    /simple\s*weather/i,
    /tkinter\s*for/i,
    /requests\s*library/i,
    /displayed\s*output/i,
    /stores?\s*and\s*display/i,
  ];
  return !rejectPatterns.some(r => r.test(trimmed));
}

// ─── Build resume object from parsed data ────────────────────────────────────
function buildResumeFromAnalysis(analysisResult, parsedData) {
  const ar = analysisResult || {};
  const pd = {
    name:     parsedData?.name     || ar.parsedData?.name     || ar.name     || "",
    role:     parsedData?.role     || ar.parsedData?.role     || ar.role     || "",
    email: (() => {
      const raw = parsedData?.email || ar.parsedData?.email || ar.email || "";
      const m = raw.match(/[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/);
      return m ? m[0] : raw;
    })(),
    phone:    parsedData?.phone    || ar.parsedData?.phone    || ar.phone    || "",
    location: parsedData?.location || ar.parsedData?.location || ar.location || "",
    linkedin: parsedData?.linkedin || ar.parsedData?.linkedin || "",
    github:   parsedData?.github   || ar.parsedData?.github   || "",
    summary:  parsedData?.summary  || ar.parsedData?.summary  || ar.summary  || "",
    skills:
      (parsedData?.skills?.length    > 0 ? parsedData.skills    : null) ||
      (ar.parsedData?.skills?.length > 0 ? ar.parsedData.skills : null) ||
      ar.matchedSkills || [],
    experience:
      (parsedData?.experience?.length    > 0 ? parsedData.experience    : null) ||
      (ar.parsedData?.experience?.length > 0 ? ar.parsedData.experience : null) ||
      ar.experience || [],
    education:
      (parsedData?.education?.length    > 0 ? parsedData.education    : null) ||
      (ar.parsedData?.education?.length > 0 ? ar.parsedData.education : null) ||
      ar.education || [],
    projects:
      (parsedData?.projects?.length    > 0 ? parsedData.projects    : null) ||
      (ar.parsedData?.projects?.length > 0 ? ar.parsedData.projects : null) ||
      ar.projects || [],
  };

  let cleanName = cleanPdfText(pd.name || "");
  if (!cleanName || /change\s*photo|^photo$|@|\d{7,}/i.test(cleanName)) cleanName = "";

  const cleanSummary = cleanPdfText(pd.summary || "")
    .replace(/^(PHOTO\s*)?(Change\s*Photo\s*)?/i, "")
    .replace(/^(Ravi\s*Kumar\s*)?(CONTACT\s*)?[n■✉]?\s*/i, "")
    .trim();

  const rawSkills = pd.skills || [];
  const cleanSkills = rawSkills
    .flatMap(s => {
      if (typeof s !== "string") return [];
      return s.split(/[,，•●■✉◆▪\n]/).map(x => x.trim()).filter(Boolean);
    })
    .filter(looksLikeSkill)
    .filter((s, i, arr) => arr.findIndex(x => x.toLowerCase() === s.toLowerCase()) === i);

  const cleanExperience = (pd.experience || []).map((exp, i) => {
    let bullets = exp.points || exp.bullets || [];
    if (typeof bullets === "string") {
      bullets = bullets.split(/\n/).map(b => b.trim()).filter(Boolean);
    }
    if (!Array.isArray(bullets)) bullets = [String(bullets)].filter(Boolean);
    bullets = bullets
      .map(b => b.replace(/^[•●■✉◆▪\-\*]\s*/, "").trim())
      .filter(b => b.length > 3);
    return {
      id:       `exp${i}`,
      role:     cleanPdfText(exp.role     || exp.title        || ""),
      company:  cleanPdfText(exp.company  || exp.organization || ""),
      duration: cleanPdfText(exp.duration || exp.period       || ""),
      bullets,
    };
  });

  const cleanEducation = (pd.education || [])
    .filter((edu, index, self) =>
      index === self.findIndex(e => JSON.stringify(e) === JSON.stringify(edu))
    )
    .map((edu, i) => {
      if (typeof edu === "string") {
        const yearMatch = edu.match(/\(?(20\d{2}\s*[-–]\s*20\d{2}|20\d{2})\)?/);
        const year = yearMatch ? yearMatch[0].replace(/[()]/g, "").trim() : "";
        const withoutYear = edu.replace(/\(?(20\d{2}\s*[-–]\s*20\d{2}|20\d{2})\)?/, "").trim();
        const parts = withoutYear.split(/[–|]/).map(s => s.trim());
        const degree = parts[0]?.replace(/[-,\s]+$/, "").trim() || edu;
        const school = parts[1]?.replace(/[-,\s]+$/, "").trim() || "";
        const cgpaMatch = edu.match(/cgpa\s*[:\-]?\s*([\d.]+)/i) || edu.match(/([\d.]+)\s*cgpa/i);
        const gpa = cgpaMatch ? cgpaMatch[1] : "";
        const percentMatch = !gpa && edu.match(/(\d{2,3})\s*%/);
        return {
          id: `edu${i}`, degree: cleanPdfText(degree),
          school: cleanPdfText(school), year,
          gpa: gpa || (percentMatch ? percentMatch[1] + "%" : ""),
          coursework: "",
        };
      }
      return {
        id:         `edu${i}`,
        degree:     cleanPdfText(edu.degree     || ""),
        school:     cleanPdfText(edu.school     || ""),
        year:       edu.year       || "",
        gpa:        edu.gpa        || "",
        coursework: edu.coursework || "",
      };
    });

  const cleanProjects = (pd.projects || [])
    .map((proj, i) => {
      let bullets = proj.points || proj.bullets || [];
      if (typeof bullets === "string") {
        bullets = bullets.split(/\n|(?<=\.)\s+(?=[A-Z])/).map(b => b.trim()).filter(Boolean);
      }
      if (!Array.isArray(bullets)) bullets = [String(bullets)].filter(Boolean);
      bullets = bullets
        .map(b => b.replace(/^[•●■✉◆▪\-\*]\s*/, "").trim())
        .filter(b => b.length > 3);
      const title = cleanPdfText(proj.title || proj.name || "");
      return { id: `proj${i}`, title, bullets, github: proj.github || "" };
    })
    .filter(p =>
      p.title &&
      p.title.length > 2 &&
      !/@/.test(p.title) &&
      !/^\d{7,}/.test(p.title) &&
      !/^(ravi|kumar|n\s|change|photo|contact)/i.test(p.title)
    );

  return {
    name:       cleanName     || "Your Name",
    title:      cleanPdfText(pd.role || "") || "Professional",
    email:      pd.email    || "",
    phone:      pd.phone    || "",
    location:   pd.location || "",
    linkedin:   pd.linkedin || "",
    github:     pd.github   || "",
    summary:    cleanSummary,
    experience: cleanExperience,
    skills:     cleanSkills,
    education:  cleanEducation,
    projects:   cleanProjects,
  };
}

// ─── Build analysis data ──────────────────────────────────────────────────────
function buildDataFromAnalysis(analysisResult, resume) {
  try {
    const ar = analysisResult || {};
    const sectionFeedback = ar.fullReport?.sectionFeedback || [];

    const sections = sectionFeedback.map((fb, i) => {
      const id = (fb.title || `section_${i}`).toLowerCase().replace(/\s+/g, "_");
      let resumeKey = "summary";
      const t = (fb.title || "").toLowerCase();
      if (t.includes("experience") || t.includes("bullet") || t.includes("work")) resumeKey = "experience";
      else if (t.includes("skill"))     resumeKey = "skills";
      else if (t.includes("education")) resumeKey = "education";
      else if (t.includes("project"))   resumeKey = "projects";

      const tagClass = fb.severity === "good" ? "good" : "warn";
      const tag = tagClass === "good" ? "Looks good" :
        (resumeKey === "skills"     ? "Missing keywords" :
         resumeKey === "experience" ? "Add metrics"      : "Needs rewrite");

      // ── Before: always from the cleaned resume object ──
      let beforeContent = "";
      if (resumeKey === "skills") {
        const skills = resume?.skills || [];
        beforeContent = skills.length > 0
          ? skills.join(", ")
          : "No skills listed in resume.";

      } else if (resumeKey === "summary") {
        beforeContent = resume?.summary || "No summary found in resume.";

      } else if (resumeKey === "experience") {
        // ── CHANGED: removed localStorage read, use resume object directly ──
        const rawExp = resume?.experience || [];
        if (rawExp.length > 0) {
          beforeContent = rawExp.map(exp => {
            const role     = exp.role     || exp.title        || "";
            const company  = exp.company  || exp.organization || "";
            const duration = exp.duration || exp.period       || "";
            const header   = [role, company, duration].filter(Boolean).join(" | ");
            const rawBullets = exp.points || exp.bullets || [];
            const bulletLines = rawBullets
              .map(b => b.replace(/^[•●■✉◆▪\-\*]\s*/, "").trim())
              .filter(b => b.length > 3)
              .join("\n");
            return bulletLines ? `${header}\n${bulletLines}` : header;
          }).join("\n\n");
        } else {
          beforeContent = resume?.summary
            ? `Based on summary:\n${resume.summary}`
            : "No experience entries found in resume.";
        }

      } else if (resumeKey === "projects") {
        // ── CHANGED: removed localStorage read, use resume object directly ──
        const rawProjects = resume?.projects || [];
        beforeContent = rawProjects.length > 0
          ? rawProjects
              .filter(p => p.title || p.name)
              .map(p => {
                const title = p.title || p.name || "";
                const rawBullets = p.points || p.bullets || [];
                const bLines = rawBullets
                  .map(b => b.replace(/^[•●■✉◆▪\-\*]\s*/, "").trim())
                  .filter(b => b.length > 3)
                  .map(b => `• ${b}`)
                  .join("\n");
                return bLines ? `${title}:\n${bLines}` : title;
              })
              .join("\n\n")
          : "No projects found in resume.";

      } else if (resumeKey === "education") {
        const edus = resume?.education || [];
        beforeContent = edus.length > 0
          ? edus.map(edu =>
              `${edu.degree || ""}${edu.school ? " — " + edu.school : ""}${edu.year ? " (" + edu.year + ")" : ""}${edu.gpa ? " | " + edu.gpa : ""}`
            ).join("\n")
          : "No education entries found.";
      }

      const rawImp = fb.improved ?? fb.improved_content ?? fb.example_improved ?? fb.rewritten ?? fb.rewrite ?? "";
      const improved = Array.isArray(rawImp) ? rawImp.join("\n") : String(rawImp || "");

      return {
        id, title: fb.title || "Section", tag, tagClass, resumeKey,
        problem:  fb.problem || "",
        fix:      fb.fix     || "",
        example:  beforeContent,
        improved,
      };
    });

    // ── Fallback sections when LLM returns nothing ──
    const fallbackSections = [];
    if (!sections.length) {
      const ats = ar.overallScore || ar.score || 50;
      if (ats < 70) {
        fallbackSections.push({
          id: "summary", title: "Professional Summary", tag: "Needs rewrite", tagClass: "warn",
          resumeKey: "summary",
          problem: "Summary is too generic — doesn't highlight role, skills, or measurable wins.",
          fix: "Open with role + years of experience + top 2 skills + one quantified achievement.",
          example: resume?.summary || "No summary found.",
          improved: "",
        });
        const expBefore = (resume?.experience || []).map(exp => {
          const header = [exp.role, exp.company, exp.duration].filter(Boolean).join(" | ");
          const bullets = (exp.bullets || []).join("\n");
          return bullets ? `${header}\n${bullets}` : header;
        }).join("\n\n");
        fallbackSections.push({
          id: "experience", title: "Work Experience", tag: "Add metrics", tagClass: "warn",
          resumeKey: "experience",
          problem: "Bullet points describe duties, not achievements. Add metrics and action verbs.",
          fix: "Format: Action verb + what you built/did + tech used + measurable result.",
          example: expBefore || "No experience entries found.",
          improved: "",
        });
      }
      if ((ar.missingSkills || []).length > 0) {
        fallbackSections.push({
          id: "skills", title: "Skills Section", tag: "Missing keywords", tagClass: "warn",
          resumeKey: "skills",
          problem: `Missing: ${(ar.missingSkills || []).slice(0, 5).join(", ")} — found in most job descriptions.`,
          fix: "Add missing skills to your skills section.",
          example: (resume?.skills || []).join(", ") || "No skills listed.",
          improved: (ar.missingSkills || []).join(", "),
        });
      }
    }

    return {
      ats:       ar.overallScore || ar.score || 0,
      match:     ar.jobMatchScore || ar.overallScore || ar.score || 0,
      verdict:   ar.summaryVerdict
        ? ar.summaryVerdict
        : (sectionFeedback.length > 0
            ? "AI analysis complete. Review suggestions below."
            : "Upload your resume and run analysis to get AI-powered feedback."),
      matched:   ar.matchedSkills || [],
      missing:   ar.missingSkills || [],
      strengths: ar.strengths     || [],
      wins:      ar.quickWins     || ar.suggestions || [],
      sections:  sections.length ? sections : fallbackSections,
    };
  } catch (err) {
    console.error("buildDataFromAnalysis error:", err);
    return { ats: 0, match: 0, verdict: "", matched: [], missing: [], strengths: [], wins: [], sections: [] };
  }
}

// ─── Templates ───────────────────────────────────────────────────────────────
const TEMPLATES = [
  { id: "classic",  name: "Classic ATS",      desc: "Single-column, max ATS compatibility.",   badge: "ATS",     badgeClass: "good" },
  { id: "modern",   name: "Two-Column Modern", desc: "Skills sidebar + experience column.",     badge: "Popular", badgeClass: "gold" },
  { id: "dark",     name: "Dark Left Panel",   desc: "Bold sidebar for creative roles.",        badge: null,      badgeClass: null },
  { id: "elegant",  name: "Elegant Centered",  desc: "Refined look for senior professionals.", badge: null,      badgeClass: null },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
function scorePillStyle(score) {
  if (score >= 70) return { background: C.greenLight, color: C.green };
  if (score >= 50) return { background: C.goldLight,  color: C.gold };
  return { background: "#fde8e5", color: C.accent };
}

function useToast() {
  const [msg, setMsg] = useState(null);
  const show = (m) => { setMsg(m); setTimeout(() => setMsg(null), 2400); };
  return [msg, show];
}

function ScorePill({ label, score }) {
  return (
    <span style={{
      ...scorePillStyle(score),
      display: "inline-flex", alignItems: "center", gap: 4,
      borderRadius: 20, padding: "3px 11px", fontSize: 12, fontWeight: 700,
      transition: "all .4s",
    }}>
      {label}: {score}
    </span>
  );
}

function SkillChip({ label, matched }) {
  return (
    <span style={{
      fontSize: 11, padding: "3px 9px", borderRadius: 12, fontWeight: 500,
      background: matched ? C.greenLight : "#fde8e5",
      color:      matched ? C.green      : C.accent,
    }}>{label}</span>
  );
}

// ─── Template styles ──────────────────────────────────────────────────────────
const COMMON_FONT = "'DM Sans', 'Segoe UI', sans-serif";
function getTemplateStyles(template) {
  const base = {
    page:         { background: "#fff", padding: "28px 32px", fontFamily: COMMON_FONT, fontSize: 12, color: "#111", lineHeight: 1.5 },
    header:       { marginBottom: 14, borderBottom: "2px solid #111", paddingBottom: 10 },
    name:         { fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px" },
    titleText:    { fontSize: 13, color: "#555", marginTop: 2 },
    contact:      { fontSize: 10, color: "#666", marginTop: 4 },
    section:      { marginTop: 14 },
    sectionTitle: { fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", borderBottom: "1px solid #ddd", paddingBottom: 3, marginBottom: 7 },
    body:         { fontSize: 11.5, color: "#333", lineHeight: 1.55 },
    jobTitle:     { fontSize: 12.5, fontWeight: 700 },
    company:      { fontSize: 11.5, color: "#555", fontStyle: "italic" },
    duration:     { fontSize: 11,   color: "#777" },
    skillChip:    { fontSize: 10.5, padding: "2px 8px", background: "#f0f0f0", borderRadius: 4, color: "#333", display: "inline-block", margin: "2px" },
    highlight:    { background: "#fffacc", outline: "1.5px solid #f5c518", borderRadius: 3 },
  };
  if (template === "modern") return {
    ...base,
    page:         { ...base.page, fontFamily: COMMON_FONT, },
    header:       { ...base.header, borderBottom: `2px solid ${C.accent}` },
    sectionTitle: { ...base.sectionTitle, color: C.accent, borderBottomColor: C.accentLight },
    skillChip:    { ...base.skillChip, background: C.accentLight, color: C.accent, borderRadius: 10 },
  };
  if (template === "dark") return {
    ...base,
    page:         { ...base.page, fontFamily: COMMON_FONT },
    header:       { ...base.header, background: "#1a2332", margin: "-28px -32px 14px", padding: "24px 32px", borderBottom: "none" },
    name:         { ...base.name, color: "#fff" },
    titleText:    { ...base.titleText, color: "#4FC3F7" },
    contact:      { ...base.contact, color: "#aaa" },
    sectionTitle: { ...base.sectionTitle, color: "#1565c0", borderBottomColor: C.blueLight },
    skillChip:    { ...base.skillChip, background: C.blueLight, color: C.blue },
  };
  if (template === "elegant") return {
    ...base,
    header:       { ...base.header, textAlign: "center", borderBottom: `1px solid ${C.accent}` },
    name:         { ...base.name, color: C.accent, fontSize: 24, fontStyle: "italic" },
    sectionTitle: { ...base.sectionTitle, color: C.accent, textAlign: "center", borderBottomColor: C.accentLight },
    skillChip:    { ...base.skillChip, background: "#fff0f0", color: C.accent, borderRadius: 12 },
  };
  return base;
}

// ─── Live Resume Preview ──────────────────────────────────────────────────────
function ResumePreview({ resume, template = "classic", addedItems = [] }) {
  const isNew = (text) => addedItems.some(a =>
    String(text).includes(a) || String(a).includes(String(text).slice(0, 20))
  );
  const s = getTemplateStyles(template);
  const contactParts = [resume.email, resume.phone, resume.location, resume.linkedin, resume.github].filter(Boolean);

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.name}>{resume.name}</div>
        {resume.title && resume.title !== "Professional" && <div style={s.titleText}>{resume.title}</div>}
        {contactParts.length > 0 && <div style={s.contact}>{contactParts.join(" • ")}</div>}
      </div>

      {resume.summary && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Professional Summary</div>
          <div style={{ ...s.body, ...(isNew(resume.summary) ? s.highlight : {}) }}>{resume.summary}</div>
        </div>
      )}

      {resume.skills?.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Skills</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            {resume.skills.map((sk, i) => (
              <span key={i} style={{ ...s.skillChip, ...(isNew(sk) ? s.highlight : {}) }}>{sk}</span>
            ))}
          </div>
        </div>
      )}

      {resume.experience?.length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Work Experience</div>
          {resume.experience.map((exp, idx) => (
            <div key={exp.id || idx} style={{ marginTop: idx > 0 ? 12 : 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <div style={s.jobTitle}>{exp.role}</div>
                <div style={s.duration}>{exp.duration}</div>
              </div>
              {exp.company && <div style={s.company}>{exp.company}</div>}
              {exp.bullets?.length > 0 && (
                <ul style={{ margin: "5px 0 0 0", paddingLeft: 16 }}>
                  {exp.bullets.map((b, i) => (
                    <li key={i} style={{ ...s.body, ...(isNew(b) ? s.highlight : {}), marginBottom: 3 }}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {resume.projects?.filter(proj => proj.title).length > 0 && (
        <div style={s.section}>
          <div style={s.sectionTitle}>Projects</div>
          {resume.projects.filter(proj => proj.title).map((proj, idx) => (
            <div key={proj.id || idx} style={{ marginTop: idx > 0 ? 10 : 0 }}>
              <div style={s.jobTitle}>{proj.title}</div>
              {proj.bullets?.length > 0 && (
                <ul style={{ margin: "5px 0 0 0", paddingLeft: 16 }}>
                  {proj.bullets.map((b, i) => (
                    <li key={i} style={{ ...s.body, ...(isNew(b) ? s.highlight : {}), marginBottom: 3 }}>{b}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {resume.education?.map((edu, idx) => (
        <div key={edu.id || idx} style={s.section}>
          {idx === 0 && <div style={s.sectionTitle}>Education</div>}
          <div style={s.jobTitle}>{typeof edu === "string" ? edu : edu.degree}</div>
          {typeof edu !== "string" && (
            <div style={s.body}>
              {[edu.school, edu.year].filter(Boolean).join(" • ")}
              {edu.gpa ? ` | ${edu.gpa}` : ""}
            </div>
          )}
          {typeof edu !== "string" && edu.coursework && edu.coursework !== "Add relevant coursework" && (
            <div style={{ ...s.body, ...(isNew("coursework") ? s.highlight : {}), marginTop: 2 }}>
              <strong>Coursework:</strong> {edu.coursework}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Template SVG Thumbnails ──────────────────────────────────────────────────
const TemplatePreviews = {
  classic: (
    <svg viewBox="0 0 160 110" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
      <rect width="160" height="110" fill="#fff" />
      <text x="80" y="14" textAnchor="middle" fill="#111" fontSize="8" fontWeight="bold" fontFamily="Georgia,serif">YOUR NAME</text>
      <text x="80" y="21" textAnchor="middle" fill="#555" fontSize="4.5" fontFamily="sans-serif">email • phone • location</text>
      <line x1="10" y1="25" x2="150" y2="25" stroke="#111" strokeWidth="1" />
      <rect x="10" y="29" width="36" height="3" rx="1" fill="#111" />
      <line x1="10" y1="34" x2="150" y2="34" stroke="#ddd" strokeWidth="0.5" />
      <rect x="10" y="37" width="130" height="2" rx="1" fill="#ccc" />
      <rect x="10" y="41" width="110" height="2" rx="1" fill="#ccc" />
      <rect x="10" y="49" width="36" height="3" rx="1" fill="#111" />
      <line x1="10" y1="54" x2="150" y2="54" stroke="#ddd" strokeWidth="0.5" />
      <rect x="10" y="57" width="80" height="2.5" rx="1" fill="#444" />
      <rect x="18" y="63" width="120" height="2" rx="1" fill="#ccc" />
      <rect x="18" y="67" width="105" height="2" rx="1" fill="#ccc" />
      <rect x="10" y="75" width="36" height="3" rx="1" fill="#111" />
      <line x1="10" y1="80" x2="150" y2="80" stroke="#ddd" strokeWidth="0.5" />
      <rect x="10" y="84" width="55" height="8" rx="3" fill="#f0f0f0" />
      <rect x="68" y="84" width="40" height="8" rx="3" fill="#f0f0f0" />
    </svg>
  ),
  modern: (
    <svg viewBox="0 0 160 110" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
      <rect width="160" height="110" fill="#fafaf8" />
      <rect x="8" y="6" width="90" height="6" rx="1" fill="#222" />
      <rect x="8" y="15" width="55" height="3" rx="1" fill="#c84b31" />
      <line x1="8" y1="24" x2="152" y2="24" stroke="#c84b31" strokeWidth="0.8" />
      <rect x="8" y="30" width="30" height="2.5" rx="1" fill="#555" />
      <rect x="8" y="36" width="75" height="2" rx="1" fill="#ccc" />
      <rect x="8" y="40" width="65" height="2" rx="1" fill="#ccc" />
      <rect x="96" y="30" width="24" height="2.5" rx="1" fill="#555" />
      <rect x="96" y="36" width="18" height="8" rx="4" fill="#faeee9" />
      <rect x="117" y="36" width="18" height="8" rx="4" fill="#faeee9" />
      <line x1="92" y1="26" x2="92" y2="100" stroke="#eee" strokeWidth="0.8" />
      <rect x="8" y="52" width="30" height="2.5" rx="1" fill="#c84b31" />
      <rect x="8" y="58" width="75" height="2" rx="1" fill="#ccc" />
    </svg>
  ),
  dark: (
    <svg viewBox="0 0 160 110" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
      <rect width="160" height="110" fill="#f4f4f2" />
      <rect x="0" y="0" width="48" height="110" fill="#1a2332" />
      <circle cx="24" cy="20" r="12" fill="#263340" stroke="#4FC3F7" strokeWidth="1" />
      <rect x="6" y="37" width="36" height="2.5" rx="1" fill="white" opacity=".9" />
      <rect x="6" y="43" width="24" height="1.5" rx="1" fill="#4FC3F7" opacity=".7" />
      <rect x="6" y="54" width="14" height="1.5" rx="1" fill="#4FC3F7" opacity=".6" />
      <rect x="6" y="58" width="36" height="1.5" rx="1" fill="white" opacity=".3" />
      <rect x="54" y="8" width="42" height="4" rx="1" fill="#222" />
      <rect x="54" y="15" width="26" height="2.5" rx="1" fill="#1565c0" opacity=".6" />
      <rect x="54" y="26" width="42" height="2.5" rx="1" fill="#333" />
      <rect x="54" y="32" width="98" height="1.5" rx="1" fill="#ccc" />
      <rect x="54" y="36" width="85" height="1.5" rx="1" fill="#ccc" />
    </svg>
  ),
  elegant: (
    <svg viewBox="0 0 160 110" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
      <rect width="160" height="110" fill="#fffef9" />
      <text x="80" y="18" textAnchor="middle" fill="#c0392b" fontSize="9" fontWeight="bold" fontFamily="Georgia,serif" fontStyle="italic">Your Name</text>
      <text x="80" y="26" textAnchor="middle" fill="#888" fontSize="5" fontFamily="sans-serif">Software Engineer • Bengaluru</text>
      <line x1="10" y1="31" x2="150" y2="31" stroke="#c0392b" strokeWidth="0.8" />
      <text x="80" y="39" textAnchor="middle" fill="#c0392b" fontSize="5.5" fontWeight="bold" fontFamily="sans-serif">SKILLS</text>
      <rect x="20" y="43" width="24" height="7" rx="4" fill="#fff0f0" />
      <rect x="47" y="43" width="24" height="7" rx="4" fill="#fff0f0" />
      <rect x="74" y="43" width="24" height="7" rx="4" fill="#fff0f0" />
      <text x="80" y="60" textAnchor="middle" fill="#c0392b" fontSize="5.5" fontWeight="bold" fontFamily="sans-serif">EXPERIENCE</text>
      <rect x="20" y="64" width="80" height="2" rx="1" fill="#ccc" />
      <rect x="20" y="69" width="65" height="2" rx="1" fill="#ccc" />
    </svg>
  ),
};

// ─── Suggestions Panel ────────────────────────────────────────────────────────
function SuggestionsTab({ data, resume, setResume, addedItems, setAddedItems, showToast,setShouldReScore,setImprovedScores  }) {
  const [openSections, setOpenSections] = useState([]);
  const toggle = (id) => setOpenSections(v => v.includes(id) ? v.filter(x => x !== id) : [...v, id]);

  useEffect(() => {
    if (data.sections?.length > 0 && openSections.length === 0) {
      setOpenSections([data.sections[0].id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.sections?.length]);

  function applyToResume(section) {
    const rawImproved = section.improved;
    const improved = Array.isArray(rawImproved)
      ? rawImproved.join("\n")
      : (rawImproved && typeof rawImproved === "object")
        ? JSON.stringify(rawImproved)
        : String(rawImproved || "");

    setResume(prev => {
      const newResume = { ...prev };

      if (section.resumeKey === "skills") {
        const newSkills = improved.split(/[,，]/).map(s => s.trim()).filter(looksLikeSkill);
        const existing  = prev.skills.map(s => s.toLowerCase());
        const toAdd     = newSkills.filter(s => !existing.includes(s.toLowerCase()));
        newResume.skills = [...prev.skills, ...toAdd];
        setAddedItems(p => [...new Set([...p, ...toAdd])]);
        showToast(`✅ ${toAdd.length} skills added to resume!`);

      } else if (section.resumeKey === "summary") {
        if (improved) {
          newResume.summary = improved;
          setAddedItems(p => [...new Set([...p, improved.slice(0, 30)])]);
          showToast("✅ Summary updated in resume!");
        } else {
          showToast("⚠ No improved summary available yet.");
        }

      } else if (section.resumeKey === "experience") {
        if (improved) {
          const bullets = improved
            .split(/\n|(?=[•●■])|(?=\d+\.\s)/)
            .map(b => b.replace(/^[•●■\-\*\d+\.]\s*/, "").trim())
            .filter(b => b.length > 10);

          if (bullets.length === 0) {
            showToast("⚠ Could not parse bullets from AI response.");
            return prev;
          }

          newResume.experience = prev.experience.map((exp, i) =>
            i === 0 ? { ...exp, bullets: [...bullets] } : { ...exp }
          );
          setAddedItems(p => [...new Set([...p, ...bullets.map(b => b.slice(0, 25))])]);
          showToast("✅ Experience bullets updated!");
        } else {
          showToast("⚠ No improved bullets available yet.");
        }

      }  else if (section.resumeKey === "projects") {
          if (improved) {
    // ✅ Split improved content by project blocks (double newline or project title pattern)
                const projectBlocks = improved
                  .split(/\n{2,}/)  // split by blank lines between projects
                  .filter(block => block.trim().length > 10);

                newResume.projects = prev.projects.map((proj, i) => {
      // ✅ Use the matching block for this project index, not all lines
                  const block = projectBlocks[i] || projectBlocks[0] || "";
      
                  const bullets = block
                    .split(/\n|(?=[•●■])|(?=\d+\.\s)/)
                    .map(l => l.replace(/^[•●■\-\*\d+\.]\s*/, "")
                    .replace(/^[^—–\-]*[—–\-]\s*/, "").trim())
                    .filter(l => l.length > 10);

                  return bullets.length > 0 ? { ...proj, bullets } : { ...proj };
    });

                const allBullets = newResume.projects.flatMap(p => p.bullets || []);
                   setAddedItems(p => [...new Set([...p, ...allBullets.map(b => b.slice(0, 25))])]);
                   showToast("✅ Projects updated with separate improvements!");
      }   else {
               showToast("⚠ No improved project content available.");
  }
      } else if (section.resumeKey === "education") {
        if (improved) {
          const lines = improved
            .split(/\n|(?=[•●■])/)
            .map(l => l.replace(/^[•●■\-\*]\s*/, "").trim())
            .filter(l => l.length > 2);

          const gpaMatch = improved.match(/GPA[:\s]*([\d.]+)/i) || improved.match(/([\d.]+)\s*\/\s*4/);
          const gpa = gpaMatch ? gpaMatch[1] : prev.education[0]?.gpa || "";

          const coursework = lines
            .filter(l => !/GPA/i.test(l) && l.length > 3)
            .join(", ");

          newResume.education = prev.education.map((edu, i) =>
            i === 0
              ? { ...edu, gpa: gpa || edu.gpa, coursework: coursework || edu.coursework }
              : { ...edu }
          );
          setAddedItems(p => [...new Set([...p, "education"])]);
          showToast("✅ Education section updated!");
        } else {
          showToast("⚠ No improved education content available yet.");
        }
      }
      setImprovedScores(null); 
      setShouldReScore(true);
      return newResume;
    });
  }

  const tagStyle = (cls) => cls === "good"
    ? { background: C.greenLight, color: C.green }
    : { background: "#fde8e5", color: C.accent };

  if (data.ats === 0 && (!data.sections || data.sections.length === 0)) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
        <div style={{ textAlign: "center", color: C.mid }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.ink, marginBottom: 6 }}>No analysis yet</div>
          <div style={{ fontSize: 13 }}>Upload your resume and run analysis to get AI-powered suggestions here.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
      {/* AI Verdict */}
      <div style={{ background: "linear-gradient(135deg,#0d0d0f,#1c2840)", color: "#fff", borderRadius: 10, padding: "13px 15px", marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontFamily: COMMON_FONT, marginBottom: 5 }}>AI Verdict</div>
        <div style={{ fontSize: 12, opacity: .85, lineHeight: 1.6 }}>{data.verdict}</div>
      </div>

      {/* Strengths */}
      {data.strengths?.length > 0 && (
        <div style={{ border: `1px solid ${C.warmGray}`, borderRadius: 10, marginBottom: 10, overflow: "hidden", background: "#fff" }}>
          <div style={{ padding: "9px 13px", background: C.greenLight, borderBottom: `1px solid ${C.warmGray}` }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.green }}>✓ Strengths</span>
          </div>
          <div style={{ padding: "10px 13px" }}>
            {data.strengths.map((s, i) => (
              <div key={i} style={{ fontSize: 12, color: C.green, marginBottom: 4 }}>✓ {s}</div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Wins */}
      {data.wins?.length > 0 && (
        <div style={{ border: `1px solid ${C.warmGray}`, borderRadius: 10, marginBottom: 10, overflow: "hidden", background: "#fff" }}>
          <div style={{ padding: "9px 13px", background: C.goldLight, borderBottom: `1px solid ${C.warmGray}` }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.gold }}>⚡ Quick Wins</span>
          </div>
          <div style={{ padding: "10px 13px" }}>
            {data.wins.slice(0, 5).map((w, i) => (
              <div key={i} style={{ fontSize: 12, color: C.mid, marginBottom: 6, paddingLeft: 12, borderLeft: `2px solid ${C.gold}` }}>{w}</div>
            ))}
          </div>
        </div>
      )}

      {/* Skills Analysis */}
      {(data.matched?.length > 0 || data.missing?.length > 0) && (
        <div style={{ border: `1px solid ${C.warmGray}`, borderRadius: 10, marginBottom: 10, overflow: "hidden", background: "#fff" }}>
          <div style={{ padding: "9px 13px", background: C.cream, borderBottom: `1px solid ${C.warmGray}`, display: "flex", alignItems: "center", gap: 7 }}>
            <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>Skills Analysis</span>
            {data.missing.filter(s => !resume.skills.some(r => r.toLowerCase() === s.toLowerCase())).length > 0 && (
              <span style={{ background: "#fde8e5", color: C.accent, fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10, textTransform: "uppercase" }}>
                {data.missing.filter(s => !resume.skills.some(r => r.toLowerCase() === s.toLowerCase())).length} missing
              </span>
            )}
          </div>
          <div style={{ padding: "10px 13px" }}>
            {data.matched.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.green, marginBottom: 5 }}>✓ Already in resume</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 12 }}>
                  {[...data.matched, ...data.missing.filter(s => resume.skills.some(r => r.toLowerCase() === s.toLowerCase()))].map(s => (
                    <SkillChip key={s} label={s} matched />
                  ))}
                </div>
              </>
            )}
            {data.missing.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, marginBottom: 6 }}>Missing — click each to add instantly ↓</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                  {data.missing.map(skill => {
                    const added = resume.skills.some(s => s.toLowerCase() === skill.toLowerCase());
                    return (
                      <button key={skill}
                        onClick={() => {
                          if (added) return;
                          setResume(r => ({ ...r, skills: [...r.skills, skill] }));
                          setAddedItems(prev => [...new Set([...prev, skill])]);
                          showToast(`✅ "${skill}" added to resume!`);
                        }}
                        style={{
                          fontSize: 12, padding: "5px 12px", borderRadius: 20, fontWeight: 600,
                          background: added ? C.greenLight : C.accentLight,
                          color:      added ? C.green      : C.accent,
                          border: `1.5px dashed ${added ? C.green : C.accent}`,
                          cursor: added ? "default" : "pointer",
                          fontFamily: COMMON_FONT, transition: "all .25s",
                        }}
                      >
                        {added ? `✓ ${skill}` : `+ ${skill}`}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => {
                    const toAdd = data.missing.filter(s => !resume.skills.some(r => r.toLowerCase() === s.toLowerCase()));
                    if (!toAdd.length) { showToast("All skills already added!"); return; }
                    setResume(r => ({ ...r, skills: [...r.skills, ...toAdd] }));
                    setAddedItems(prev => [...new Set([...prev, ...toAdd])]);
                    showToast(`✅ All ${toAdd.length} missing skills added to resume!`);
                  }}
                  style={{
                    background: C.accent, color: "#fff", border: "none", borderRadius: 8,
                    padding: "7px 16px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                    fontFamily: COMMON_FONT, width: "100%",
                  }}
                >
                  + Add All Missing Skills to Resume
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Section Feedback Cards */}
      {data.sections?.map(section => {
        const open = openSections.includes(section.id);
        return (
          <div key={section.id} style={{ border: `1px solid ${C.warmGray}`, borderRadius: 10, marginBottom: 10, overflow: "hidden", background: "#fff" }}>
            <div
              onClick={() => toggle(section.id)}
              style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 13px", cursor: "pointer", background: C.cream }}
            >
              <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{section.title}</span>
              <span style={{ ...tagStyle(section.tagClass), fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10, textTransform: "uppercase" }}>
                {section.tag}
              </span>
              <span style={{ color: C.mid, fontSize: 13, transition: "transform .2s", transform: open ? "rotate(0deg)" : "rotate(-90deg)", display: "inline-block" }}>▾</span>
            </div>
            {open && (
              <div style={{ borderTop: `1px solid ${C.warmGray}`, padding: "11px 13px" }}>
                {section.problem && (
                  <div style={{ color: C.accent, fontSize: 11.5, marginBottom: 6 }}>⚠ {section.problem}</div>
                )}
                {section.fix && (
                  <div style={{ color: C.green, fontSize: 11.5, marginBottom: 10, padding: "6px 10px", background: C.greenLight, borderRadius: 6 }}>
                    💡 {section.fix}
                  </div>
                )}
                {(section.example || section.improved) && (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: section.example && section.improved ? "1fr 1fr" : "1fr",
                    gap: 8, marginBottom: 10,
                  }}>
                    {section.example && (
                      <div style={{ background: "#fff5f5", borderRadius: 7, padding: "8px 10px" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: C.mid, marginBottom: 4, letterSpacing: ".5px" }}>Before</div>
                        <div style={{ fontSize: 10.5, color: C.mid, lineHeight: 1.6, whiteSpace: "pre-line" }}>{section.example}</div>
                      </div>
                    )}
                    {section.improved && (
                      <div style={{ background: C.greenLight, borderRadius: 7, padding: "8px 10px" }}>
                        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: C.green, marginBottom: 4, letterSpacing: ".5px" }}>AI Improved</div>
                        <div style={{ fontSize: 10.5, color: C.green, lineHeight: 1.6, whiteSpace: "pre-line" }}>{section.improved}</div>
                      </div>
                    )}
                  </div>
                )}
                {section.improved && (
                  <button
                    onClick={() => applyToResume(section)}
                    style={{
                      background: C.accent, color: "#fff", border: "none", borderRadius: 7,
                      padding: "8px 18px", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                      fontFamily: COMMON_FONT, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    }}
                  >
                    ✦ Apply Improved Version → Resume Updates Live
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}

      {(!data.sections || data.sections.length === 0) && data.ats > 0 && (
        <div style={{ textAlign: "center", padding: 24, color: C.green, background: C.greenLight, borderRadius: 10 }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}>🎉</div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Your resume looks strong!</div>
          <div style={{ fontSize: 12, marginTop: 4, color: C.mid }}>No major section issues detected by AI analysis.</div>
        </div>
      )}
    </div>
  );
}

// ─── Templates Tab ────────────────────────────────────────────────────────────
function TemplatesTab({ resume, showToast, jobRole, jobDescription })  {
  const [selected,    setSelected]    = useState("modern");
  const [downloading, setDownloading] = useState(false);
  const previewRef = useRef(null);

  async function downloadFromBackend() {
    const node = previewRef.current;
    if (!node) { showToast("❌ Preview not ready."); return; }
    setDownloading(true);
    showToast("⏳ Generating PDF from your updated resume…");
    try {
      const canvas = await html2canvas(node, {
        scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false,
      });
      const imgData     = canvas.toDataURL("image/png");
      const PDF_W       = 210;
      const PDF_H       = 297;
      const pxPerMm     = canvas.width / PDF_W;
      const imgHeightMm = canvas.height / pxPerMm;
      const pdf         = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      if (imgHeightMm <= PDF_H) {
        pdf.addImage(imgData, "PNG", 0, 0, PDF_W, imgHeightMm);
      } else {
        let yOffset = 0;
        while (yOffset < imgHeightMm) {
          if (yOffset > 0) pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, -yOffset, PDF_W, imgHeightMm);
          yOffset += PDF_H;
        }
      }
      const safeName = (resume.name || "resume").replace(/[^a-zA-Z0-9_\- ]/g, "").trim().replace(/\s+/g, "_");
      pdf.save(`${safeName}_${selected}_resume.pdf`);
      showToast("✅ Resume downloaded with all improvements!");
    } catch (err) {
      console.error(err);
      showToast("❌ PDF generation failed.");
    } finally {
      setDownloading(false);
    }
  }

  const badgeStyle = (cls) => cls === "good"
    ? { background: C.greenLight, color: C.green }
    : { background: C.goldLight,  color: C.gold };
    

  return (
    
    <div style={{ flex: 1, display: "flex", flexDirection: "row", overflow: "hidden"}}>

      <div style={{ width: "52%", display: "flex", flexDirection: "column", borderRight: `1px solid ${C.warmGray}`, overflow: "hidden" }}>
        <div style={{ padding: "8px 14px", background: C.cream, borderBottom: `1px solid ${C.warmGray}`, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".6px", color: C.mid, flexShrink: 0 }}>
          Choose Template
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {TEMPLATES.map(tmpl => (
              <div key={tmpl.id} onClick={() => setSelected(tmpl.id)}
                style={{
                  border: `2px solid ${selected === tmpl.id ? C.accent : C.warmGray}`,
                  borderRadius: 10, cursor: "pointer",
                  boxShadow: selected === tmpl.id ? `0 0 0 3px ${C.accentLight}` : "none",
                  background: "#fff", overflow: "hidden", transition: "all .18s",
                }}
              >
                <div style={{ height: 115, background: C.cream, padding: 6 }}>{TemplatePreviews[tmpl.id]}</div>
                <div style={{ padding: "8px 10px", borderTop: `1px solid ${C.warmGray}` }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 5 }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700 }}>{tmpl.name}</div>
                      <div style={{ fontSize: 10.5, color: C.mid, marginTop: 1 }}>{tmpl.desc}</div>
                    </div>
                    {tmpl.badge && (
                      <span style={{ ...badgeStyle(tmpl.badgeClass), fontSize: 9.5, fontWeight: 700, padding: "2px 6px", borderRadius: 7, flexShrink: 0 }}>
                        {tmpl.badge}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ padding: "10px 14px", borderTop: `1px solid ${C.warmGray}`, background: "#fff", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <div style={{ flex: 1, fontSize: 11.5, color: C.mid }}>
            Selected: <strong style={{ color: C.ink }}>{TEMPLATES.find(t => t.id === selected)?.name}</strong>
          </div>
          <button onClick={downloadFromBackend} disabled={downloading}
            style={{
              background: C.accent, color: "#fff", border: "none", borderRadius: 8,
              padding: "9px 20px", fontSize: 13, fontWeight: 600,
              cursor: downloading ? "not-allowed" : "pointer",
              fontFamily: COMMON_FONT, opacity: downloading ? 0.7 : 1,
            }}
          >
            {downloading ? "⏳ Generating…" : "⬇ Download PDF"}
          </button>
        </div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "8px 14px", background: C.cream, borderBottom: `1px solid ${C.warmGray}`, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".6px", color: C.mid, flexShrink: 0 }}>
          Preview — {TEMPLATES.find(t => t.id === selected)?.name}
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 16, background: "#eae9e3" }}>
          <div ref={previewRef} style={{ boxShadow: "0 2px 20px rgba(0,0,0,0.13)", borderRadius: 6, overflow: "hidden" }}>
            <ResumePreview resume={resume} template={selected} addedItems={[]} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Loading / Error Screens ──────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: C.paper, fontFamily: "'DM Sans','Segoe UI',sans-serif", gap: 16 }}>
      <div style={{ width: 40, height: 40, border: `3px solid ${C.warmGray}`, borderTop: `3px solid ${C.accent}`, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <div style={{ fontSize: 15, color: C.mid, fontWeight: 500 }}>Analyzing your resume…</div>
      <div style={{ fontSize: 12, color: C.muted }}>This takes about 10–15 seconds</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function ErrorScreen({ message }) {
  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: C.paper, fontFamily: "'DM Sans','Segoe UI',sans-serif", gap: 12 }}>
      <div style={{ fontSize: 36 }}>⚠️</div>
      <div style={{ fontSize: 15, color: C.accent, fontWeight: 600, maxWidth: 320, textAlign: "center" }}>{message}</div>
      <button onClick={() => window.history.back()} style={{ marginTop: 8, background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "9px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: COMMON_FONT }}>
        ← Go Back
      </button>
    </div>
  );
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function ResumeEditor({
  analysisResult: propAnalysis,
  parsedData: propParsedData,
  jobRole: propJobRole = "",
  jobDescription: propJobDesc = "",
  onBack,
}) {
  const location = useLocation();

  const storedAnalysis = JSON.parse(localStorage.getItem("resume_analysis_data") || "null");

  const initialAnalysis = propAnalysis || location.state?.analysisResult || null;

  const initialParsedData =
    propParsedData ||
    location.state?.parsedData ||
    location.state?.analysisResult?.parsedData ||
    null;

  const jobRole        = propJobRole || location.state?.jobRole        || localStorage.getItem("job_role")        || "";
  const jobDescription = propJobDesc || location.state?.jobDescription || localStorage.getItem("job_description") || "";

  const [analysisResult, setAnalysisResult] = useState(initialAnalysis);
  const [parsedData,     setParsedData]     = useState(initialParsedData);
  const [isLoading,      setIsLoading]      = useState(!initialAnalysis);
  const [fetchError,     setFetchError]     = useState(null);
  const [resume,         setResume]         = useState(() => buildResumeFromAnalysis(initialAnalysis, initialParsedData));
  const [activeTab,      setActiveTab]      = useState("suggestions");
  const [addedItems,     setAddedItems]     = useState([]);
  const [toast,          showToast]         = useToast();
  const [improvedScores, setImprovedScores] = useState(null);
  const [shouldReScore,  setShouldReScore]  = useState(false);


  // Add this useEffect at the top of ResumeEditor
useEffect(() => {
  // Subscribe to file changes from the store
  const unsubscribe = onResumeFileChange((newFile) => {
    if (newFile) {
      setImprovedScores(null);   // ← clear stale scores
      setShouldReScore(false);   // ← reset flag
    }
  });
  return unsubscribe; // cleanup on unmount
}, []);

  async function fetchImprovedScores(currentAts = 0) {
  try {
    const file = getResumeFile();
    if (!file) { console.warn("No resume file — skipping rescore"); return; }

    const formData = new FormData();
    formData.append("resume", file);
    formData.append("jobDescription", jobDescription || "");
    formData.append("jobRole", jobRole || "");
    formData.append("originalScore", String(currentAts || analysisResult?.overallScore || 0));

    const res = await api.post("/api/resume/analyze-improved", formData);
      const raw = res.data;
    console.log("🔥 Setting improvedScores:", raw.ats_score, raw.job_match_score);
    setImprovedScores({
     ats:   raw.ats_score,
     match: raw.job_match_score,
    });
    console.log("✅ improvedScores SET");

      if (raw) {
        const originalAts = raw.original_ats_score || analysisResult?.overallScore || 0;

        const MAX_BOOST = 15;
        const rawAts   = raw.ats_score       || raw.overallScore  || 0;
        const rawMatch = raw.job_match_score || raw.jobMatchScore || 0;

        const cappedAts   = originalAts > 0 ? Math.min(rawAts,   originalAts + MAX_BOOST)     : rawAts;
        const cappedMatch = originalAts > 0 ? Math.min(rawMatch, originalAts + MAX_BOOST + 5) : rawMatch;

        setImprovedScores({
          ats:           cappedAts,
          match:         cappedMatch,
          originalAts,
          improvement:   cappedAts - originalAts,
          justification: raw.score_justification     || "",
          recognized:    raw.improvements_recognized || [],
          remaining:     raw.remaining_gaps          || [],
        });
      }
    } catch (err) {
      console.error("fetchImprovedScores error:", err);
    }
  }

  useEffect(() => {
    console.log("🔥 CURRENT improvedScores:", improvedScores);
  }, [improvedScores]);

  useEffect(() => {
     console.log("👀 shouldReScore changed:", shouldReScore);
  if (!shouldReScore) return;
  setShouldReScore(false);       
  fetchImprovedScores(analysisResult?.overallScore || 0);  
}, [shouldReScore]);

  useEffect(() => {
    if (initialAnalysis) return;

    async function fetchAnalysis() {
      try {
        const file = getResumeFile();
        if (!file) {
          setFetchError("No resume file found. Please upload your resume first.");
          setIsLoading(false);
          return;
        }

        const formData = new FormData();
        formData.append("resume",         file);
        formData.append("jobDescription", jobDescription);
        formData.append("jobRole",        jobRole);

        
        const res = await api.post("/api/resume/analyze", formData);
        const raw = res.data;

        const structured = {
          overallScore:    raw.overallScore    || 0,
          jobMatchScore:   raw.jobMatchScore   || 0,
          atsParsing:      raw.atsParsing      || "FAIL",
          jobMatch:        raw.jobMatch        || "LOW",
          recruiterImpact: raw.recruiterImpact || "LOW",
          parsedData:      raw.parsedData      || {},
          matchedSkills:   raw.matchedSkills   || [],
          missingSkills:   raw.missingSkills   || [],
          strengths:       raw.strengths       || [],
          quickWins:       raw.quickWins       || [],
          summaryVerdict:  raw.summaryVerdict  || "",
          suggestions:     raw.suggestions     || [],
          preview:         raw.preview         || {},
          fullReport:      raw.fullReport      || {},
        };

        localStorage.setItem("resume_analysis_data", JSON.stringify(structured));

        setAnalysisResult(structured);
        setParsedData(structured.parsedData);

      } catch (err) {
        console.error("analyze API error:", err);
        setFetchError("Resume analysis failed. Please go back and try again.");
      } finally {
        setIsLoading(false);
      }
    }

    fetchAnalysis();
  }, []);

  useEffect(() => {
    if (analysisResult) {
      setResume(buildResumeFromAnalysis(analysisResult, parsedData));
    }
  }, [analysisResult, parsedData]);

  const data = useMemo(() => buildDataFromAnalysis(analysisResult, resume), [analysisResult, resume]);

  // ── Live score calculation ──
  const addedSkillsCount = useMemo(() => {
    if (!data.missing?.length) return 0;
    const missingLower = data.missing.map(s => s.toLowerCase());
    return addedItems.filter(i => missingLower.includes(i.toLowerCase())).length;
  }, [addedItems, data.missing]);

  const appliedSectionsCount = useMemo(() => {
    const sectionKeys = new Set();
    if (resume.summary && addedItems.some(i => i.length >= 20 && resume.summary.startsWith(i))) {
      sectionKeys.add("summary");
    }
    const expBullets = resume.experience?.[0]?.bullets || [];
    if (expBullets.length > 0 && addedItems.some(i => expBullets.some(b => b.startsWith(i)))) {
      sectionKeys.add("experience");
    }
    const projBullets = resume.projects?.flatMap(p => p.bullets || []) || [];
    if (projBullets.length > 0 && addedItems.some(i => projBullets.some(b => b.startsWith(i)))) {
      sectionKeys.add("projects");
    }
    return sectionKeys.size;
  }, [addedItems, resume]);

  const storedAts   = storedAnalysis?.overallScore  || 0;
  const storedMatch = storedAnalysis?.jobMatchScore || 0;
  const baseAts     = Math.max(data.ats   || 0, storedAts);
  const baseMatch   = Math.max(data.match || 0, storedMatch);

  const totalMissing      = Math.max(data.missing?.length || 1, 1);
  const skillBoostAts     = Math.round((addedSkillsCount / totalMissing) * 18);
  const skillBoostMatch   = Math.round((addedSkillsCount / totalMissing) * 28);
  const sectionBoostAts   = appliedSectionsCount * 5;
  const sectionBoostMatch = appliedSectionsCount * 6;

  const liveAts   = improvedScores?.ats   ?? (baseAts   > 0 ? Math.min(92, baseAts   + skillBoostAts   + sectionBoostAts)   : 0);
  const liveMatch = improvedScores?.match ?? (baseMatch > 0 ? Math.min(92, baseMatch + skillBoostMatch + sectionBoostMatch) : 0);

  const TABS = [
    { id: "suggestions", label: "💡 AI Suggestions" },
    { id: "templates",   label: "🎨 Templates & Download" },
  ];

  if (isLoading)  return <LoadingScreen />;
  if (fetchError) return <ErrorScreen message={fetchError} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: C.paper, fontFamily: "'DM Sans','Segoe UI',sans-serif", color: C.ink, fontSize: 14 }}>

      {/* Top bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "0 20px", height: 52, background: "#fff", borderBottom: `1px solid ${C.warmGray}`, flexShrink: 0 }}>
        {resume.name && resume.name !== "Your Name" && (
          <span style={{ fontSize: 12, color: C.mid, fontStyle: "italic" }}>{resume.name}</span>
        )}
        {addedItems.length > 0 && (
          <span style={{ background: C.greenLight, color: C.green, fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 10 }}>
            ✓ {addedItems.length} improvements applied
          </span>
        )}
        <div style={{ flex: 1 }} />
        {liveAts   > 0 && <ScorePill label="ATS"   score={liveAts}   />}
        {liveMatch > 0 && <ScorePill label="Match" score={liveMatch} />}
        {onBack && (
          <button
            onClick={onBack}
            style={{ background: "none", border: `1.5px solid ${C.warmGray}`, borderRadius: 7, padding: "5px 13px", fontSize: 12.5, cursor: "pointer", color: C.mid, fontFamily: COMMON_FONT }}
          >
            ← Back
          </button>
        )}
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", background: "#fff", borderBottom: `1px solid ${C.warmGray}`, padding: "0 18px", flexShrink: 0 }}>
        {TABS.map(t => (
          <div
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            style={{
              padding: "10px 16px", fontSize: 13, fontWeight: 500, cursor: "pointer",
              color:        activeTab === t.id ? C.accent : C.mid,
              borderBottom: activeTab === t.id ? `2px solid ${C.accent}` : "2px solid transparent",
              marginBottom: -1, transition: "all .15s",
            }}
          >
            {t.label}
          </div>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
        {activeTab === "suggestions" && (
          <>
            <div style={{ width: "44%", display: "flex", flexDirection: "column", borderRight: `1px solid ${C.warmGray}`, background: "#eae9e3", overflow: "hidden" }}>
              <div style={{ padding: "8px 12px", background: C.cream, borderBottom: `1px solid ${C.warmGray}`, display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".6px", color: C.mid, flex: 1 }}>
                  📄 Your Resume — Live Preview
                </span>
                {addedItems.length > 0 && (
                  <span style={{ fontSize: 10, color: C.green, fontWeight: 600 }}>✓ {addedItems.length} changes</span>
                )}
              </div>
              <div style={{ flex: 1, overflowY: "auto", padding: 14 }}>
                <div style={{ boxShadow: "0 2px 16px rgba(0,0,0,0.12)", borderRadius: 6, overflow: "hidden" }}>
                  <ResumePreview resume={resume} template="classic" addedItems={addedItems} />
                </div>
              </div>
            </div>

            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
              <div style={{ padding: "8px 14px", background: C.cream, borderBottom: `1px solid ${C.warmGray}`, fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".6px", color: C.mid, flexShrink: 0 }}>
                AI Suggestions — click any button to update resume instantly →
              </div>
              <SuggestionsTab
                data={data}
                resume={resume}
                setResume={setResume}
                addedItems={addedItems}
                setAddedItems={setAddedItems}
                showToast={showToast}
                setShouldReScore={setShouldReScore}
                setImprovedScores={setImprovedScores}
              />
              <div style={{ background: "#fff", borderTop: `1px solid ${C.warmGray}`, padding: "10px 18px", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <span style={{ fontSize: 12, color: C.mid, flex: 1 }}>Apply improvements → see them live on left → then download</span>
                <button
                  onClick={async () => {
                    setActiveTab("templates");
                    showToast("⏳ Re-scoring your improved resume…");
                    await fetchImprovedScores(data.ats);
                    showToast("✅ Scores updated with your improvements!");
                  }}
                  style={{ background: C.accent, color: "#fff", border: "none", borderRadius: 8, padding: "9px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: COMMON_FONT }}
                >
                  Choose Template & Download →
                </button>
              </div>
            </div>
          </>
        )}

        {activeTab === "templates" && (
          <TemplatesTab
            resume={resume}
            showToast={showToast}
            jobRole={jobRole}
            jobDescription={jobDescription}
            improvedScores={improvedScores}
          />
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 22, left: "50%", transform: "translateX(-50%)", background: C.ink, color: "#fff", padding: "9px 22px", borderRadius: 20, fontSize: 13, fontWeight: 500, zIndex: 9999, pointerEvents: "none" }}>
          {toast}
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #c2bdb4; border-radius: 3px; }
      `}</style>
    </div>
  );
}